'use client'

import { Plus, X } from 'lucide-react'
import { useState } from 'react'
import { motion } from 'framer-motion'
import { cn } from '@/utils/styles/cn'
import type { IFaqItem } from '@/components/ui/faq-accordion'

/**
 * lp-v3 FAQ accordion — the shared single-open accordion behind every v3 FAQ. Two visual variants:
 * `cards` (12px-radius #FDFBF8 cards with lucide +/× icons — home & pricing) and `rows`
 * (hairline-divided editorial rows with orange +/− glyphs — partner program).
 *
 * The shared invariant, regardless of variant: ONE item open at a time (first item open by
 * default), and every answer is ALWAYS mounted in the DOM — it only collapses visually — so the
 * full FAQ ships in the SSR HTML and stays SEO-readable. `!` beats the `.ds2` element typography
 * that wraps the landing shell.
 */
export function LpFaqAccordion({
    items,
    variant,
    className,
}: {
    items: readonly IFaqItem[]
    variant: 'cards' | 'rows'
    className?: string
}) {
    const [openIndex, setOpenIndex] = useState<number | null>(0)

    return (
        <div className={cn('flex flex-col', variant === 'cards' && 'gap-3', className)}>
            {items.map((item, index) => {
                const isOpen = openIndex === index
                const toggle = () => setOpenIndex(isOpen ? null : index)
                return variant === 'cards' ? (
                    <CardItem key={item.question} item={item} isOpen={isOpen} onToggle={toggle} />
                ) : (
                    <RowItem key={item.question} item={item} isOpen={isOpen} onToggle={toggle} />
                )
            })}
            {/* Rows close on a final hairline so the last answer doesn't hang open-ended. */}
            {variant === 'rows' && <div className="border-t border-[rgba(19,18,16,0.1)]" />}
        </div>
    )
}

interface IFaqItemProps {
    item: IFaqItem
    isOpen: boolean
    onToggle: () => void
}

/** Answer wrapper shared by both variants: always mounted, only height/opacity animate. */
function AnswerReveal({ isOpen, children }: { isOpen: boolean; children: React.ReactNode }) {
    return (
        <motion.div
            initial={false}
            animate={{ height: isOpen ? 'auto' : 0, opacity: isOpen ? 1 : 0 }}
            transition={{
                height: { duration: 0.25, ease: [0.4, 0, 0.2, 1] },
                opacity: { duration: 0.2 },
            }}
            className="overflow-hidden"
            aria-hidden={!isOpen}
        >
            {children}
        </motion.div>
    )
}

function CardItem({ item, isOpen, onToggle }: IFaqItemProps) {
    return (
        <div
            // 12px radius, #FDFBF8 card, 24px padding all sides (per design spec);
            // soft shadow so the card reads against the cream page background.
            className="cursor-pointer select-none rounded-[12px] bg-[#FDFBF8] p-6 shadow-[0_1px_3px_rgba(19,18,16,0.07)]"
            onClick={onToggle}
        >
            <button
                type="button"
                aria-expanded={isOpen}
                className="flex w-full items-center justify-between gap-4 text-left"
            >
                <h3 className="text-[15px]! font-normal leading-[1.3]! tracking-normal! text-[#131210] md:text-[16px]!">
                    {item.question}
                </h3>
                <span className="shrink-0 text-[#131210]">
                    {isOpen ? <X className="size-4" /> : <Plus className="size-4" />}
                </span>
            </button>

            <AnswerReveal isOpen={isOpen}>
                <div onClick={(event) => event.stopPropagation()} style={isOpen ? { cursor: 'text' } : undefined}>
                    <p className="whitespace-pre-line pt-4 text-[14px]! leading-[1.5]! text-[#746C64]">{item.answer}</p>
                </div>
            </AnswerReveal>
        </div>
    )
}

function RowItem({ item, isOpen, onToggle }: IFaqItemProps) {
    return (
        <div className="border-t border-[rgba(19,18,16,0.1)]">
            <button
                type="button"
                aria-expanded={isOpen}
                onClick={onToggle}
                className="flex w-full cursor-pointer items-baseline justify-between gap-5 py-[22px] text-left"
            >
                <span className="text-[19px]! font-normal leading-[1.3]! tracking-[-0.01em]! text-[#131210]">
                    {item.question}
                </span>
                <span className="flex-none text-[16px]! leading-none! text-[#FF5500]">{isOpen ? '−' : '+'}</span>
            </button>

            <AnswerReveal isOpen={isOpen}>
                <p className="max-w-[720px] pb-6 pr-10 text-[16px]! leading-[1.6]! text-[#746C64]">{item.answer}</p>
            </AnswerReveal>
        </div>
    )
}
